// Environment variable helper
// In development: Uses process.env from webpack (via .env files)
// In production: Uses window._env_ from runtime-generated env-config.js
export const getEnv = (key) => {
  // Check if runtime environment exists (Docker/production)
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  // Fallback to build-time environment (development/local builds)
  return process.env[key];
};

/**
 * NOTE: Stripe pricing is now fetched from backend API via Redux.
 * See: src/redux/features/configuration/configurationSlice.js
 *
 * Components should use useProductPlans() hook (src/hooks/useProductPlans.js)
 * instead of PRODUCT_PLANS constant to get plans with real-time pricing from Redux.
 */

/**
 * The name of the main application.
 *
 * This variable contains the primary title used for the application and is used throughout various components
 * to reference or display the name of the application.
 *
 * Value: 'Remote Job'
 */
export const MAIN_APP_NAME = 'Remote Job';
/**
 * The base endpoint URL for user-related API operations.
 *
 * This value is typically sourced from the environment variable `REACT_APP_BASE_END_POINT_USER`.
 * It is used as the foundational part of the API URLs to interact with user data on the server.
 * Make sure the environment variable is correctly set before using this constant.
 */
export const BASE_END_POINT_USER = getEnv('REACT_APP_BASE_END_POINT_USER');
/**
 * BASE_END_POINT_JOB represents the base URL for the job-related API endpoints.
 * It is configured through the environment variable REACT_APP_BASE_END_POINT_JOB.
 * This variable is used to make network requests to the job-related services.
 */
export const BASE_END_POINT_JOB = getEnv('REACT_APP_BASE_END_POINT_JOB');
/**
 * BASE_END_POINT_INVOICE is a constant that stores the URL endpoint string for invoice-related API calls.
 * This value is retrieved from the environment variable REACT_APP_BASE_END_POINT_INVOICE at runtime.
 */
export const BASE_END_POINT_INVOICE = getEnv(
  'REACT_APP_BASE_END_POINT_INVOICE'
);
/**
 * The base endpoint URL for company-related API calls,
 * sourced from the environment variable REACT_APP_BASE_END_POINT_COMPANY.
 * This variable is used to configure the base URL for
 * API requests that pertain to company data and services.
 *
 * @constant {string} BASE_END_POINT_COMPANY
 */
export const BASE_END_POINT_COMPANY = getEnv(
  'REACT_APP_BASE_END_POINT_COMPANY'
);

export const BASE_END_POINT_NOTIFICATION = getEnv(
  'REACT_APP_BASE_END_POINT_NOTIFICATION'
);
export const BASE_END_POINT_NOTIFICATION_WS = getEnv(
  'REACT_APP_BASE_END_POINT_NOTIFICATION_WS'
);
/**
 * The base endpoint URL for messaging-related API calls.
 * This variable holds the base URL for the messaging API.
 *
 * @constant {string} BASE_END_POINT_MESSAGES
 */
export const BASE_END_POINT_MESSAGES =
  getEnv('REACT_APP_BASE_END_POINT_MESSAGES') ||
  getEnv('REACT_APP_BASE_END_POINT_JOB');
/**
 * The base endpoint URL for messaging WebSocket connections.
 * This variable holds the WebSocket URL for real-time messaging.
 *
 * @constant {string} BASE_END_POINT_MESSAGES_WS
 */
export const BASE_END_POINT_MESSAGES_WS =
  getEnv('REACT_APP_BASE_END_POINT_MESSAGES_WS') ||
  (getEnv('REACT_APP_BASE_END_POINT_JOB')
    ? getEnv('REACT_APP_BASE_END_POINT_JOB').replace('http', 'ws') +
      '/ws/messages'
    : 'ws://localhost:8080/ws/messages');

/**
 * The base endpoint URL for interview-related API calls.
 * This variable holds the base URL for the interview API.
 *
 * @constant {string} BASE_END_POINT_INTERVIEW
 */
export const BASE_END_POINT_INTERVIEW =
  getEnv('REACT_APP_BASE_END_POINT_INTERVIEW') ||
  getEnv('REACT_APP_BASE_END_POINT_JOB');
/**
 * This variable holds the base endpoint file path for the application.
 * It is initialized from the environment variable `REACT_APP_BASE_END_POINT_FILE`.
 * Used to configure the base API endpoint for network requests.
 *
 * @type {string}
 */
export const BASE_END_POINT_FILE = getEnv('REACT_APP_BASE_END_POINT_FILE');

export const BASE_END_POINT_PLAN = getEnv('REACT_APP_BASE_END_POINT_PLAN');

/**
 * The base endpoint URL for general API operations.
 * This is the main API endpoint used by some components.
 * Falls back to the job endpoint if not specified.
 *
 * @constant {string} BASE_END_POINT
 */
export const BASE_END_POINT =
  getEnv('REACT_APP_BASE_END_POINT') || getEnv('REACT_APP_BASE_END_POINT_JOB');

/**
 * Google reCAPTCHA v3 site key
 * Used for bot protection in login and registration forms
 *
 * @constant {string} RECAPTCHA_SITE_KEY
 */
export const RECAPTCHA_SITE_KEY = '6LeQJTgsAAAAAFe_pwOXnSwcJ-M0V8z9G08Lkr8h';

/**
 * Error message indicating the absence of a JWT access token.
 * This message is used when an API call is attempted without providing
 * the necessary JWT for authentication.
 */
export const EMPTY_JWT_ERROR_MESSAGE =
  'There is no JWT access token to make calls to the APIs';
/**
 * Represents the error message to be used when the API returns a 401 Unauthorized status.
 *
 * This message indicates that the authentication credentials provided are
 * either missing or invalid, and the client must take corrective action to gain
 * access to the requested resource.
 */
export const STATUS_401_ERROR_MESSAGE =
  'Calls to the API return 401 Unauthorized';
/**
 * A constant string that holds the error message used when the API
 * returns a 400 Bad Request status code.
 */
export const STATUS_400_ERROR_MESSAGE =
  'Calls to the API return 400 Bad Request';
/**
 * Constant string that holds the error message for a 403 Forbidden response from the API.
 *
 * STATUS_403_ERROR_MESSAGE is used to indicate that the client's request was valid,
 * but the server is refusing to respond due to insufficient permissions.
 */
export const STATUS_403_ERROR_MESSAGE = 'Calls to the API return 403 Forbidden';
/**
 * A constant string representing the error message when API calls return a 500 Internal Server Error.
 */
export const STATUS_500_ERROR_MESSAGE =
  'Calls to the API return 500 Internal Server Error';
/**
 * Message indicating that calls to the API returned a 504 Gateway Timeout error.
 * This typically means that the server, acting as a gateway or proxy, did not receive a timely response from the upstream server.
 */
export const STATUS_504_ERROR_MESSAGE =
  'Calls to the API return 504 Gateway Timeout';
/**
 * DEPRECATED: Stripe configuration is now fetched from backend API via Redux.
 * Use stripePublishableKeySelector from configurationSlice instead.
 *
 * @deprecated This constant is deprecated and will be removed in a future version.
 * Components should use useSelector(stripePublishableKeySelector) to get the Stripe key from Redux.
 */
export const STRIPE_CONFIG = {
  publishable_key: getEnv('REACT_APP_STRIPE_PUBLISHABLE_KEY') || null
};
/**
 * Constant representing the status of a successful payment
 * in the Stripe payment system.
 *
 * @constant {string}
 */
export const STRIPE_PAYMENT_STATUS_SUCCEEDED = 'succeeded';
/**
 * This constant represents the interval for product plans, specifically set
 * to a monthly duration. It is used to identify and differentiate plans that
 * are billed or managed on a monthly basis.
 *
 * @constant {string}
 * @default 'month'
 */
export const PRODUCT_PLANS_INTERVAL_MONTH = 'month';
/**
 * A constant representing the type option for product plans.
 * This specific type option is set to 'check', which may be used
 * to signify a boolean or a verification condition for product plan options.
 *
 * @constant {string}
 * @name PRODUCT_PLANS_TYPE_OPTION_CHECK
 */
export const PRODUCT_PLANS_TYPE_OPTION_CHECK = 'check';
/**
 * A constant representing the type of option for product plans,
 * indicating that the available choices are displayed as radio buttons.
 *
 * This is typically used in user interfaces where a user needs to
 * select one option from a predefined set of available plans or options.
 *
 * Example:
 * - "Basic", "Standard", and "Premium" are displayed as radio buttons
 *   for the user to choose from.
 *
 * @constant {string}
 */
export const PRODUCT_PLANS_TYPE_OPTION_RADIO = 'radio';
/**
 * The `PRODUCT_PLANS_TYPE_COLOR_PICKER` constant represents a specific type of product plan associated
 * with a color picker tool used in the application. This string is used to identify and differentiate
 * this plan type from other types within the system.
 *
 * @constant {string}
 */
export const PRODUCT_PLANS_TYPE_COLOR_PICKER = 'colorPicker';

export const PLAN_TYPE = {
  POST_JOB: 1,
  SHOW_COMPANY_LOGO: 2,
  GEO_LOCK: 3,
  HIGHLIGHT_YELLOW: 4,
  STICKY_TOP_24_HOURS: 5,
  STICKY_TOP_1_WEEK: 6,
  STICKY_TOP_1_MONTH: 7,
  HIGHLIGHT_COMPANY_COLOR: 8,
  // Hiring Profile Bundles
  HIRING_PROFILE_STARTER: 9,
  HIRING_PROFILE_PROFESSIONAL: 10,
  HIRING_PROFILE_PREMIUM: 11
};

/**
 * Base product plan configuration (without pricing).
 * Static data for product plans. Pricing is fetched dynamically from Redux.
 * Use useProductPlans() hook to get plans with pricing populated from API.
 *
 * Properties:
 * - id: A unique identifier for the product plan (PLAN_TYPE constant).
 * - title: The title of the product plan.
 * - extraInfoChips: An array of extra information chips that provide additional highlights or recommendations.
 * - benefits: A list of benefits included in the product plan.
 * - isDefaultChecked: A boolean indicating if the plan is selected by default.
 * - isDefaultDisabled: A boolean indicating if the plan is disabled by default.
 * - typeOption: The type of option used for selection (e.g., check, radio, color picker).
 */
export const PRODUCT_PLANS_BASE = [
  {
    id: PLAN_TYPE.POST_JOB,
    title: 'Post a job (+450)',
    extraInfoChips: [],
    benefits: [
      'Highlight your ad',
      '2x Better position in searching',
      'Up to 2 extra posts'
    ],
    isDefaultChecked: true,
    isDefaultDisabled: true,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_CHECK
  },
  {
    id: PLAN_TYPE.SHOW_COMPANY_LOGO,
    title: 'Show my ⭐️ company logo besides my post (+$44)',
    extraInfoChips: [
      {
        text: '2X MORE VIEWS',
        highlight: false
      },
      {
        text: 'HIGHLY RECOMMENDED',
        highlight: true
      }
    ],
    benefits: [
      'Highlight your ad',
      '2x Better position in searching',
      'Up to 2 extra posts'
    ],
    isDefaultChecked: true,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_CHECK
  },
  {
    id: PLAN_TYPE.HIGHLIGHT_YELLOW,
    title: 'Highlight your post in ⚠️ yellow (+$49)',
    extraInfoChips: [
      {
        text: '2x MORE VIEWS',
        highlight: false
      }
    ],
    benefits: [
      'Highlight your ad',
      '3x Better position in searching',
      'Up to 5 extra posts',
      'Pin ad for 7 days on main page'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_CHECK
  },
  {
    id: PLAN_TYPE.STICKY_TOP_24_HOURS,
    title:
      'Sticky your post so it stays on 📌 top of the frontpage for ⏰ 24 hours (+$359)',
    extraInfoChips: [
      {
        text: '2x MORE VIEWS',
        highlight: false
      }
    ],
    benefits: [
      'Highlight your ad',
      '3x Better position in searching',
      'Up to 5 extra posts',
      'Pin ad for 7 days on main page'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_RADIO
  },
  {
    id: PLAN_TYPE.STICKY_TOP_1_WEEK,
    title:
      'Sticky your post so it stays on 📌 top of the frontpage for 🗓 1 entire week (+$899)',
    extraInfoChips: [
      {
        text: '6x MORE VIEWS',
        highlight: false
      }
    ],
    benefits: [
      'Highlight your ad',
      '3x Better position in searching',
      'Up to 5 extra posts',
      'Pin ad for 7 days on main page'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_RADIO
  },
  {
    id: PLAN_TYPE.STICKY_TOP_1_MONTH,
    title:
      'Sticky your post so it stays on 📌 top of the frontpage for 🗓 1 entire month (+$2,697)',
    extraInfoChips: [
      {
        text: '9x MORE VIEWS',
        highlight: false
      }
    ],
    benefits: [
      'Highlight your ad',
      '3x Better position in searching',
      'Up to 5 extra posts',
      'Pin ad for 7 days on main page'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_RADIO
  },
  {
    id: PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR,
    title: "Highlight with your company's 🌈 brand color (+$499)",
    extraInfoChips: [
      {
        text: '3x MORE VIEWS',
        highlight: false
      }
    ],
    benefits: [
      'Highlight your ad',
      '3x Better position in searching',
      'Up to 5 extra posts',
      'Pin ad for 7 days on main page'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_COLOR_PICKER
  },
  {
    id: PLAN_TYPE.GEO_LOCK,
    title:
      "Geolock your post for people only in the location it's restricted to above and block applicants from elsewhere (+$99)",
    extraInfoChips: [
      {
        text: '10X SEGMENTATION',
        highlight: false
      }
    ],
    benefits: [
      'Block applicants from outside your specified regions',
      'Ensure compliance with location-specific hiring requirements',
      'Target candidates in your preferred geographic areas',
      'Automatic location detection and enforcement'
    ],
    isDefaultChecked: false,
    isDefaultDisabled: false,
    typeOption: PRODUCT_PLANS_TYPE_OPTION_CHECK
  }
];

/**
 * @deprecated Use useProductPlans() hook instead.
 * This constant is kept for backward compatibility but will be removed in a future version.
 * The hook provides plans with real-time pricing from the API.
 */
export const PRODUCT_PLANS = PRODUCT_PLANS_BASE;

/**
 * Determines whether all data should be mocked.
 *
 * The value of this variable is determined by the environment variable
 * `REACT_APP_MOCK_ALL_DATA`. If this environment variable is set to the
 * string 'true', `MOCK_ALL_DATA` will be `true`, otherwise it will be `false`.
 * This can be used during the development process to bypass actual data
 * fetching and use mock data instead.
 *
 * @type {boolean}
 */
export const MOCK_ALL_DATA = getEnv('REACT_APP_MOCK_ALL_DATA') === 'true';
/**
 * The root path of the application.
 *
 * This constant typically represents the base directory or entry point of the application.
 * It can be used for resolving file paths or routing within the application.
 *
 * @constant {string}
 */
export const APP_PATH_ROOT = '/';
/**
 * The URL path for the dashboard section of the application.
 *
 * @constant {string}
 */
export const APP_PATH_DASHBOARD = '/dashboard';
/**
 * Constant representing the path used for editing company details.
 * This path is used in the application routing mechanism to direct
 * the user to the company edit page.
 */
export const APP_PATH_EDITOR_COMPANY = '/company/editor';
/**
 * The URL path template for viewing a company's details.
 *
 * This string represents the route used in the application to access
 * the view for a specific company, based on the company's unique identifier.
 * The `:id` segment of the path acts as a placeholder for dynamic values,
 * allowing for the retrieval of specific company information.
 * The optional `:slug?` segment provides SEO-friendly URL with company name.
 *
 * Example usage: '/company/123/google' where '123' is the company ID and 'google' is the slug.
 * Legacy URLs without slug still work: '/company/123'
 */
export const APP_PATH_VIEW_COMPANY = '/company/:id/:slug?';
/**
 * The URL path indicating the successful subscription plan page.
 *
 * This constant holds the path that directs users to a
 * confirmation page after they have successfully subscribed
 * to a plan.
 *
 * @constant {string} APP_PATH_SUCCESS_SUBSCRIPTION_PLAN
 */
export const APP_PATH_SUCCESS_SUBSCRIPTION_PLAN = '/plan-success';
/**
 * Represents the application path for the account route.
 *
 * This constant stores the relative URL path for the account
 * section of the application.
 */
export const APP_PATH_ACCOUNT = '/account';
/**
 * Represents the path to the jobs section of the application.
 * This constant contains the string value used to navigate to or reference the jobs-related endpoints or views within the app.
 * Generally used in routing or API endpoint construction.
 *
 * @constant {string}
 * @name APP_PATH_JOBS
 */
export const APP_PATH_JOBS = '/search-jobs';
/**
 * The application path for creating a new job.
 *
 * This constant holds the URI path segment that leads to the page
 * or endpoint where a new job can be created within the application.
 *
 * @constant {string}
 */
export const APP_PATH_EDITOR_JOB = '/job/editor';
/**
 * The relative URL path used in the application for navigating to the job edit page.
 * This variable contains the endpoint for accessing the job editing functionality.
 */
export const APP_PATH_EDIT_JOB = '/job/edit';
/**
 * The route path used to view a specific job details in the application.
 *
 * :id is a placeholder for the job ID which will be dynamically replaced
 * with the actual job ID at runtime.
 * The optional `:slug?` segment provides SEO-friendly URL with job title and company.
 *
 * Example: '/job/123/senior-developer-at-google' where '123' is the job ID.
 * Legacy URLs without slug still work: '/job/123'
 *
 * @type {string}
 */
export const APP_PATH_JOB_VIEW = '/job/:id/:slug?';
/**
 * A constant holding the path for the login page of the application.
 * This path is used to navigate to the sign-in screen.
 *
 * @constant {string}
 */
export const APP_PATH_LOGIN = '/sign-in';
/**
 * Contains the endpoint path for the sign-up page.
 *
 * The constant APP_PATH_SIGNUP is used to direct users to the sign-up
 * page of the application. This path should be used wherever the sign-up
 * route is needed to ensure consistency and maintainability.
 *
 * @constant {string} APP_PATH_SIGNUP
 */
export const APP_PATH_SIGNUP = '/sign-up';
/**
 * The path for the account activation page in the application.
 *
 * This variable holds the relative URL path where users are redirected
 * to activate their accounts via email link. It is used throughout the
 * application to handle email verification workflows.
 *
 * @constant {string} APP_PATH_ACTIVATION
 */
export const APP_PATH_ACTIVATION = '/activate';
/**
 * The path for the resend activation email page in the application.
 *
 * This variable holds the relative URL path where users can request
 * a new activation email if they didn't receive it or if it expired.
 *
 * @constant {string} APP_PATH_RESEND_ACTIVATION
 */
export const APP_PATH_RESEND_ACTIVATION = '/resend-activation';
/**
 * The path for the registration success page in the application.
 *
 * This variable holds the relative URL path where users are redirected
 * after successfully creating an account. It displays instructions for
 * email verification and next steps.
 *
 * @constant {string} APP_PATH_REGISTRATION_SUCCESS
 */
export const APP_PATH_REGISTRATION_SUCCESS = '/registration-success';
/**
 * The path for the forgot password page in the application.
 *
 * This variable holds the relative URL path where users can request
 * a password reset email if they forgot their password.
 *
 * @constant {string} APP_PATH_FORGOT_PASSWORD
 */
export const APP_PATH_FORGOT_PASSWORD = '/forgot-password';
/**
 * The path for the reset password page in the application.
 *
 * This variable holds the relative URL path where users can set a new
 * password after clicking the reset link in their email.
 *
 * @constant {string} APP_PATH_RESET_PASSWORD
 */
export const APP_PATH_RESET_PASSWORD = '/reset-password';
/**
 * The path for the terms and conditions page in the application.
 *
 * This variable holds the relative URL path where users can find the terms and conditions
 * content. It is used throughout the application to redirect or link users to this page.
 */
export const APP_PATH_TERM_CONDITIONS = '/terms-and-conditions';
/**
 * A constant representing the URL endpoint for successful subscription callbacks.
 * This URL is used within the application to redirect or handle actions after
 * a user has successfully subscribed.
 *
 * @type {string}
 */
export const SUBSCRIPTION_SUCCESS_CALLBACK = '/success';
/**
 * Constant representing the URL path for the package plan feature within the application.
 *
 * This path is typically used to navigate to or reference the package planning section
 * in client-side routing or when constructing URLs for API requests related to package
 * plan functionalities.
 *
 * @constant {string} APP_PATH_PACKAGE_PLAN
 * @default '/plan/package'
 */
export const APP_PATH_PACKAGE_PLAN = '/plan/package';
export const APP_PATH_JOB_ADDONS = '/plan/job-addons';
/**
 * Constant representing the URL path for viewing current plans/subscription.
 *
 * This path is used to navigate to the user's current plans page where
 * they can view their active Hiring Profile and manage their subscription.
 *
 * @constant {string} APP_PATH_PLANS
 * @default '/plans'
 */
export const APP_PATH_PLANS = '/plans';
/**
 * Constant representing the URL path for plan configuration page.
 *
 * This path is used to navigate to the plan configuration page where
 * Premium users can customize their plan settings (e.g., brand color).
 *
 * @constant {string} APP_PATH_PLAN_CONFIGURE
 * @default '/job/:jobId/plan/configure'
 */
export const APP_PATH_PLAN_CONFIGURE = '/job/:jobId/plan/configure';
/**
 * Constant representing the URL path for the pricing page.
 *
 * This path is used to navigate to the pricing/sales page that showcases
 * all available plans and their benefits to encourage users to post jobs.
 *
 * @constant {string} APP_PATH_PRICING
 * @default '/pricing'
 */
export const APP_PATH_PRICING = '/pricing';

/**
 * Constant representing the URL path for the products page.
 *
 * This path is used to navigate to the products page that showcases
 * platform features and offerings.
 *
 * @constant {string} APP_PATH_PRODUCTS
 * @default '/products'
 */
export const APP_PATH_PRODUCTS = '/products';

/**
 * Constant representing the URL path for the AI explainer page.
 *
 * @constant {string} APP_PATH_AI
 * @default '/ai'
 */
export const APP_PATH_AI = '/ai';

/**
 * Constant representing the URL path for the FAQ - Job Seekers page.
 *
 * @constant {string} APP_PATH_FAQ_JOB_SEEKERS
 * @default '/faq/job-seekers'
 */
export const APP_PATH_FAQ_JOB_SEEKERS = '/faq/job-seekers';

/**
 * Constant representing the URL path for the FAQ - Employers page.
 *
 * @constant {string} APP_PATH_FAQ_EMPLOYERS
 * @default '/faq/employers'
 */
export const APP_PATH_FAQ_EMPLOYERS = '/faq/employers';

/**
 * Constant representing the URL path for the startups page.
 *
 * This path is used to navigate to the startups information page
 * which helps startups understand how to hire remote talent through the platform.
 *
 * @constant {string} APP_PATH_STARTUPS
 * @default '/startups'
 */
export const APP_PATH_STARTUPS = '/startups';
/**
 * Constant representing the URL path for the about page.
 *
 * This path is used to navigate to the about page
 * which provides information about Remote Job's mission and values.
 *
 * @constant {string} APP_PATH_ABOUT
 * @default '/about'
 */
export const APP_PATH_ABOUT = '/about';
/**
 * Constant representing the URL path for the user type selection page.
 *
 * This path is used to navigate to the user type selection page
 * where users choose whether they are a company or a candidate.
 *
 * @constant {string} APP_PATH_USER_TYPE_SELECTION
 * @default '/user-type-selection'
 */
export const APP_PATH_USER_TYPE_SELECTION = '/user-type-selection';
export const APP_PATH_APPLICATIONS = '/applications';
export const APP_PATH_MY_APPLICATIONS = '/my-applications';
export const APP_PATH_CANDIDATE_PROFILE = '/profile';
export const APP_PATH_VIEW_CANDIDATE_PROFILE = '/candidate/:userId';
export const APP_PATH_ONBOARDING = '/onboarding';
export const APP_PATH_COMPANY_ONBOARDING = '/company/onboarding';
export const APP_VERSION_RELEASE = 'V12.6';
/**
 * Constant representing the URL path for the messages page.
 *
 * @constant {string} APP_PATH_MESSAGES
 * @default '/messages'
 */
export const APP_PATH_MESSAGES = '/messages';
/**
 * Constant representing the URL path for the interviews page.
 * @constant {string} APP_PATH_INTERVIEWS
 * @default '/interviews'
 */
export const APP_PATH_INTERVIEWS = '/interviews';
export const APP_PATH_SUPPORT = '/support';
export const APP_PATH_SUGGESTIONS = '/suggestions';
/**
 * Constant URL endpoint for job-related API requests.
 *
 * `JOB_END_POINT` is used to define the base URL path for all job-related operations
 * in the API version 1. It typically helps in making HTTP requests to perform
 * CRUD operations on job entities.
 *
 * Example usage in an application may include fetching job details,
 * creating new job entries, updating existing job data, or deleting jobs.
 *
 * Note: This value should be used strategically in conjunction with other path
 * segments and query parameters to form complete API request URLs.
 *
 * @constant {string}
 */
export const JOB_END_POINT = '/api/v1/job';
/**
 * Represents the API endpoint to manage user-related operations.
 * This endpoint is used to perform CRUD operations on user resources.
 *
 * @constant {string}
 */
export const USER_END_POINT = '/api/v1/user';
/**
 * The endpoint for accessing company-related API routes.
 *
 * This constant defines the base URL endpoint for making requests
 * to the company-related API. It is used throughout the application
 * to standardize and centralize the API endpoint, ensuring that any
 * changes to the endpoint structure can be easily managed in one place.
 *
 * @constant {string} COMPANY_END_POINT - The base URL endpoint for company-related API.
 */
export const COMPANY_END_POINT = '/api/v1/company';

export const NOTIFICATION_END_POINT = '/api/v1/notification';
/**
 * Constant URL endpoint for messaging-related API requests.
 *
 * @constant {string}
 */
export const MESSAGES_END_POINT = '/api/v1/messages';
/**
 * The endpoint for interview-related API requests.
 * @constant {string} INTERVIEW_END_POINT
 */
export const INTERVIEW_END_POINT = '/api/interviews';
/**
 * The endpoint URL for file-related API requests.
 *
 * This constant is used to define the specific path for API calls that handle
 * file operations such as uploading, downloading, deleting, and retrieving file information.
 *
 * Usage of this endpoint should ensure that all file-related actions are centralized and consistent
 * throughout the application.
 *
 * Path: /api/v1/file
 */
export const FILE_END_POINT = '/api/v1/file';
/**
 * The endpoint for uploading files.
 * This URL is used as the target destination for file upload operations,
 * where files will be sent via HTTP POST requests.
 */
export const UPLOAD_FILE_END_POINT = '/upload';
/**
 * The API endpoint for retrieving user file data.
 *
 * This constant represents the endpoint used to access and manage
 * user files within the back-end service. It is a string containing
 * the relative path to the user file resource.
 *
 * @constant {string}
 * @default '/user'
 */
export const GET_USER_FILE_END_POINT = '/user';
/**
 * The endpoint for accessing invoice-related operations in the API.
 *
 * This constant defines the relative URL used to perform CRUD operations on invoices,
 * such as creating, reading, updating, and deleting invoice records. The endpoint adheres
 * to the version 1 (v1) of the API.
 *
 * @constant {string} INVOICE_END_POINT
 * @default '/api/v1/invoice'
 */
export const INVOICE_END_POINT = '/api/v1/invoice';

export const PLAN_END_POINT = '/api/v1/plan';

/**
 * The endpoint to be used for handling subscription approval requests.
 * This constant holds the URL path segment for the subscription approval functionality.
 * Typically used in API calls to approve subscription requests.
 */
export const SUBSCRIPTION_APPROVAL_END_POINT = '/subscription/approval';
/**
 * The endpoint for processing subscription-related requests.
 *
 * This constant holds the URL path for the subscription processing endpoint,
 * which typically handles the logic for initiating and managing user subscriptions in the system.
 */
export const SUBSCRIPTION_PROCESS_END_POINT = '/subscription/process';
/**
 * The endpoint URL for creating a new subscription.
 *
 * This variable holds a string representing the URL that clients
 * should use to send requests for initiating a new user
 * subscription process.
 */
export const CREATE_SUBSCRIPTION_END_POINT = '/create-subscription';
/**
 * The endpoint URL for creating a one-time payment.
 *
 * This constant defines the path used to initiate a one-time payment within
 * the system. It is typically used in API calls to handle immediate payment processing.
 */
export const CREATE_PAYMENT_END_POINT = '/create-payment';
/**
 * A constant representing the event or status when an API call fails within the application.
 * The variable holds a string value 'APP_API_CALL_FAIL', which can be used to handle
 * failure scenarios, trigger error handling mechanisms, log errors, or display
 * user notifications related to failed API calls.
 */
export const APP_API_CALL_FAIL = 'APP_API_CALL_FAIL';
/**
 * A list of job categories, each represented by an object
 * containing a `value` and a `label`.
 *
 * @type {Array<{value: string, label: string}>}
 * @property {string} value - The identifier for the job category.
 * @property {string} label - The display name for the job category.
 *
 * Example entries:
 * - { value: 'dev', label: 'Software Development' }
 * - { value: 'customer_support', label: 'Customer Support' }
 */
export const CATEGORIES_JOB = [
  {
    value: 'dev',
    label: 'Software Development'
  },
  {
    value: 'customer_support',
    label: 'Customer Support'
  },
  {
    value: 'sales',
    label: 'Sales'
  },
  {
    value: 'marketing',
    label: 'Marketing'
  },
  {
    value: 'design',
    label: 'Design'
  },
  {
    value: 'front_end',
    label: 'Front End'
  },
  {
    value: 'backend',
    label: 'Back End'
  },
  {
    value: 'legal',
    label: 'Legal'
  },
  {
    value: 'testing',
    label: 'Testing'
  },
  {
    value: 'quality_assurance',
    label: 'Quality Assurance'
  },
  {
    value: 'non tech',
    label: 'Non-Tech'
  },
  {
    value: 'other',
    label: 'Other'
  }
];
/**
 * `REGIONS_COUNTRIES` is an array containing strings representing regions and countries with respective emojis.
 *
 * The list includes major geographical regions and individual countries prefixed by their corresponding emoji.
 * - Regions: Africa, Asia, Europe, Latin America, Middle East, North America, Oceania, Worldwide.
 * - Countries: Includes countries from across the globe such as Afghanistan, Albania, Algeria, etc.
 */
export const REGIONS_COUNTRIES = [
  '🦁 Africa',
  '⛩ Asia',
  '🇪🇺 Europe',
  '💃 Latin America',
  '🕌 Middle East',
  '⛰️ North America',
  '🌊 Oceania',
  '🌏 Worldwide',
  '🇦🇫 Afghanistan',
  '🇦🇱 Albania',
  '🇩🇿 Algeria',
  '🇦🇸 American Samoa',
  '🇦🇩 Andorra',
  '🇦🇴 Angola',
  '🇦🇮 Anguilla',
  '🇦🇶 Antarctica',
  '🇦🇬 Antigua and Barbuda',
  '🇦🇷 Argentina',
  '🇦🇲 Armenia',
  '🇦🇼 Aruba',
  '🇦🇺 Australia',
  '🇦🇹 Austria',
  '🇦🇿 Azerbaijan',
  '🇧🇭 Bahrain',
  '🇧🇩 Bangladesh',
  '🇧🇧 Barbados',
  '🇧🇾 Belarus',
  '🇧🇪 Belgium',
  '🇧🇿 Belize',
  '🇧🇯 Benin',
  '🇧🇲 Bermuda',
  '🇧🇹 Bhutan',
  '🇧🇴 Bolivia',
  '🇧🇦 Bosnia',
  '🇧🇼 Botswana',
  '🇧🇻 Bouvet Island',
  '🇧🇷 Brazil',
  '🇮🇴 British Indian Ocean Territory',
  '🇻🇬 British Virgin Islands',
  '🇧🇳 Brunei',
  '🇧🇬 Bulgaria',
  '🇧🇫 Burkina Faso',
  '🇧🇮 Burundi',
  '🇰🇭 Cambodia',
  '🇨🇲 Cameroon',
  '🇨🇦 Canada',
  '🇨🇻 Cape Verde',
  '🇧🇶 Caribbean Netherlands',
  '🇰🇾 Cayman Islands',
  '🇨🇫 Central African Republic'
];
/**
 * An array of job types, each represented as an object with `value` and `label` properties.
 *
 * - `value`: A string identifier for the job type.
 * - `label`: A human-readable string representing the job type.
 */
export const TYPES_JOB = [
  {
    value: 'full_time',
    label: 'Full-Time'
  },
  {
    value: 'part_time',
    label: 'Part-time'
  },
  {
    value: 'contractor',
    label: 'Contractor'
  },
  {
    value: 'temporary',
    label: 'Temporary'
  },
  {
    value: 'intern',
    label: 'Internship'
  },
  {
    value: 'per_diem',
    label: 'Per diem'
  },
  {
    value: 'volunteer',
    label: 'Volunteer'
  }
];
/**
 * An array containing a list of employee benefits offered by the company.
 * Each benefit is represented as a string, often with an associated emoji for quick visual reference.
 *
 * Example benefits include:
 * - 401(k) plans
 * - Vision, dental, and medical insurance
 * - Unlimited vacation and paid time off
 * - Async work and a distributed team
 * - Company retreats and coworking budgets
 * - Learning budgets and gym memberships
 * - Home office setup and mental wellness budgets
 * - Unique perks such as pay in crypto, profit sharing, and equity compensation
 * - Policies like no whiteboard interviews, no monitoring system, and no workplace politics
 * - Inclusivity with hiring practices accommodating all ages
 */
export const BENEFITS = [
  '💰 401(k)',
  '🌎 Distributed team',
  '⏰ Async',
  '🤓 Vision insurance',
  '🦷 Dental insurance',
  '🚑 Medical insurance',
  '🏖 Unlimited vacation',
  '🏖 Paid time off',
  '📆 4 day workweek',
  '💰 401k matching',
  '🏔 Company retreats',
  '🏬 Coworking budget',
  '📚 Learning budget',
  '💪 Free gym membership',
  '🧘 Mental wellness budget',
  '🖥 Home office budget',
  '🥧 Pay in crypto',
  '💰 Profit sharing',
  '💰 Equity compensation',
  '⬜️ No whiteboard interview',
  '👀 No monitoring system',
  '🚫 No politics at work',
  '🎅 We hire old (and young)'
];
/**
 * A constant representing the type of subscription card used for email subscriptions.
 * This can be used to identify or categorize subscription methods within the system.
 */
export const EMAIL_SUBSCRIPTION_CARD = 'email';
/**
 * The name identifier for subscription cards.
 * This constant is used to represent the
 * 'name' attribute in subscription card objects.
 * The value of this constant is a string.
 */
export const NAME_SUBSCRIPTION_CARD = 'name';
/**
 * Represents the state indicating whether a draft is valid.
 *
 * This variable typically holds a string that is used as a key or flag to indicate
 * the validity of a draft within an application. Being a constant, it ensures that
 * the key's value remains consistent and immutable across different parts of the code.
 */
export const IS_VALID_DRAFT = 'isValidDraft';
/**
 * @constant {string} PLAN_BILL
 *
 * @description
 * Represents a constant identifier for a specific billing plan in the system.
 * This value is used as a key to reference and manage billing plans associated
 * with user accounts or services.
 */
export const PLAN_BILL = 'planBill';
/**
 * The billing amount for the basic subscription plan.
 *
 * The value is set to `299.0`, representing the cost for the basic plan
 * in the specified currency, typically USD. It is used to calculate
 * the total payment amount for users who opt for the basic subscription.
 */
export const BASIC_PLAN_PRICE_BILL = 299.0;
/**
 * Represents the price of the "Good Plan" in the billing system.
 *
 * @constant {number}
 */
export const GOOD_PLAN_PRICE_BILL = 59.0;
/**
 * @constant {number} BETTER_PLAN_PRICE_BILL
 * @description The price (in dollars) for the 'Better' subscription plan's billing cycle.
 */
export const BETTER_PLAN_PRICE_BILL = 89.0;
/**
 * Represents the price of the best plan currently available.
 * This value is used to store the billing amount for the best subscription plan.
 *
 * @type {number}
 * @constant
 */
export const BEST_PLAN_PRICE_BILL = 139.0;
/**
 * Constant representing the billing plan name for the basic plan.
 * This value is used to identify the basic subscription tier in the billing system.
 *
 * @constant {string}
 */
export const BASIC_PLAN_BILL = 'Basic';
/**
 * A constant representing a billing plan with a good rating.
 * This value is used to denote a billing plan classified as 'Good.'
 * Can be utilized for comparison or display purposes.
 */
export const GOOD_PLAN_BILL = 'Good';
/**
 * A constant representing the 'Better' plan bill.
 *
 * BETTER_PLAN_BILL is a predefined string that can be used
 * to identify or set the plan to 'Better' in billing-related
 * operations or configurations.
 *
 * @constant {string}
 */
export const BETTER_PLAN_BILL = 'Better';
/**
 * BEST_PLAN_BILL indicates the designation for the optimal plan billing.
 * This constant can be used to identify or set the billing category to 'Best',
 * which may represent the most cost-effective or feature-rich plan available.
 */
export const BEST_PLAN_BILL = 'Best';
/**
 * An object that maps different subscription plans to their corresponding prices.
 * The keys in this object represent the plan identifiers (e.g., 'basic', 'premium', 'enterprise'),
 * and the values represent the price for each plan.
 *
 * @type {Object.<string, number>}
 */
export const PRICES_BY_PLAN = {};
PRICES_BY_PLAN[BASIC_PLAN_BILL] = BASIC_PLAN_PRICE_BILL;
PRICES_BY_PLAN[GOOD_PLAN_BILL] = GOOD_PLAN_PRICE_BILL;
PRICES_BY_PLAN[BETTER_PLAN_BILL] = BETTER_PLAN_PRICE_BILL;
PRICES_BY_PLAN[BEST_PLAN_BILL] = BEST_PLAN_PRICE_BILL;
/**
 * Constant representing the type of invoice related to a subscription bill.
 * This is used to categorize and identify invoices that are specifically for subscription services.
 *
 * @constant {string}
 * @default 'subscriptionBill'
 * @readonly
 */
export const INVOICE_SUBSCRIPTION_BILL = 'subscriptionBill';
/**
 * The URL for approving subscription invoices.
 *
 * This constant is used to store the endpoint that clients will use
 * to approve their subscription-related invoices. It is crucial for
 * the workflow of subscription management, ensuring that the approval
 * process is streamlined and consistent.
 */
export const INVOICE_SUBSCRIPTION_APPROVAL_URL = 'subscriptionApprovalUrl';
/**
 * A constant that represents the subscription event for when an invoice is processed.
 * This constant can be used to subscribe to notifications or events that trigger when
 * an invoice processing state changes.
 *
 * @constant {string} INVOICE_IS_PROCESSED_SUBSCRIPTION
 */
export const INVOICE_IS_PROCESSED_SUBSCRIPTION = 'isProcessedSubscription';
